import { Injectable } from '@angular/core';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DevicesDataService } from '../../common/services/device/devices-data.service';
import { hydrateFilterSnapshot } from '../../common/services/filter-snapshot/filter-snapshot-hydration';
import { FilterBarId } from '../../common/services/route-config/models/route-config.model';
import { RouteConfigService } from '../../common/services/route-config/services/route-config.service';
import {
  FILTER_SNAPSHOT_REF_PARAM,
  UrlStateManagerService,
} from '../../common/services/url-state-manager/url-state-manager.service';
import * as FilterActions from './filter.actions';
import { selectCurrentRoute } from './filter.selectors';

/** EDUS-2575: device-search NgRx filter-bar id (single source of truth). */
const DEVICE_SEARCH_FILTER_BAR_ID = FilterBarId.DeviceSearch;

@Injectable()
export class FilterEffects {
  autoConfigEffect$: Observable<Action>;
  loadFiltersFromUrl$: Observable<Action>;
  updateFiltersInUrl$: Observable<Action>;
  syncSnapshotRefToUrl$: Observable<unknown>;

  private isUpdatingUrl = false;

  constructor(
    private actions$: Actions,
    private urlStateManager: UrlStateManagerService,
    private store: Store,
    private devicesDataService: DevicesDataService,
    private routeConfigService: RouteConfigService,
    private snackbar: SnackbarService
  ) {
    // Auto-config effect that sets up route configs
    this.autoConfigEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(() => {
          // If URL is being updated programmatically, skip loading and reset flag
          if (this.isUpdatingUrl) {
            this.isUpdatingUrl = false;
            return false;
          }
          return true;
        }),
        map((action: RouterNavigatedAction) => {
          const _route = action.payload.routerState.url.split('?')[0];
          const queryParams = action.payload.routerState.root.queryParams;

          return FilterActions.loadFiltersFromUrl({ queryParams });
        })
      )
    );

    this.loadFiltersFromUrl$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FilterActions.loadFiltersFromUrl),
        switchMap((action) => {
          const route = window.location.pathname;

          // EDUS-2575 restore-on-load: a `?filterSnapshotRef` link expands the
          // stored filter set server-side; fetch it, hydrate the chips (partial
          // restore on config drift), and seed the ref so the grid queries by
          // ref. 404 → graceful empty state, never a crash.
          const ref = action.queryParams[FILTER_SNAPSHOT_REF_PARAM];
          if (typeof ref === 'string' && ref.length > 0) {
            return this.devicesDataService.getFilterSnapshot(ref).pipe(
              map((res) => {
                const settings =
                  this.routeConfigService.getFilterBarConfig(
                    route,
                    DEVICE_SEARCH_FILTER_BAR_ID
                  ) ?? [];
                const { restored, skipped } = hydrateFilterSnapshot(
                  res.payload,
                  settings
                );
                if (skipped > 0) {
                  this.snackbar.showMessage(
                    'Some filters from this link are no longer available'
                  );
                }
                return FilterActions.loadFiltersFromUrlSuccess({
                  filterBars: { [DEVICE_SEARCH_FILTER_BAR_ID]: restored },
                  filterSnapshotRefs: { [DEVICE_SEARCH_FILTER_BAR_ID]: ref },
                });
              }),
              catchError(() => {
                this.snackbar.showError(
                  'This shared filter link is no longer available'
                );
                // Clear the store ref (loadFiltersFromUrlSuccess) AND strip the
                // dead `?filterSnapshotRef` from the URL so it isn't copyable:
                // setFilterSnapshotRef(null) drives syncSnapshotRefToUrl$ →
                // writeSnapshotRefParam(null), which removes the param (review
                // item 6).
                return of(
                  FilterActions.setFilterSnapshotRef({
                    filterBarId: DEVICE_SEARCH_FILTER_BAR_ID,
                    snapshotRef: null,
                  }),
                  FilterActions.loadFiltersFromUrlSuccess({
                    filterBars: { [DEVICE_SEARCH_FILTER_BAR_ID]: [] },
                    filterSnapshotRefs: {
                      [DEVICE_SEARCH_FILTER_BAR_ID]: null,
                    },
                  })
                );
              })
            );
          }

          const filterBars = this.urlStateManager.deserializeFromUrl(
            route,
            action.queryParams
          );
          return of(FilterActions.loadFiltersFromUrlSuccess({ filterBars }));
        })
      )
    );

    // EDUS-2575: when the data-fetch path sets/clears the snapshot ref, mirror
    // it to the URL (write `?filterSnapshotRef` + drop the inline filter param,
    // or remove it) so the address bar is the small shareable artifact and the
    // URL agrees with the grid. isUpdatingUrl guards the loadFiltersFromUrl loop.
    this.syncSnapshotRefToUrl$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(FilterActions.setFilterSnapshotRef),
          tap((action) => {
            this.isUpdatingUrl = true;
            this.urlStateManager
              .writeSnapshotRefParam(
                action.filterBarId ?? DEVICE_SEARCH_FILTER_BAR_ID,
                action.snapshotRef
              )
              // When the router ignores the navigation (URL unchanged or a
              // guard blocks it), no ROUTER_NAVIGATED follows to reset the
              // guard — so reset it here, otherwise the flag stays stuck `true`
              // and the next real URL→filter load is silently skipped (review
              // item 5). On a successful navigation we must NOT reset here:
              // autoConfigEffect$ resets it when ROUTER_NAVIGATED arrives, and
              // resetting early would re-open the load loop.
              .then((navigated) => {
                if (!navigated) {
                  this.isUpdatingUrl = false;
                }
              })
              .catch(() => {
                this.isUpdatingUrl = false;
              });
          })
        ),
      { dispatch: false }
    );

    this.updateFiltersInUrl$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FilterActions.updateFilters),
        withLatestFrom(this.store.select(selectCurrentRoute)),
        switchMap(([action, currentRoute]) => {
          // Only set isUpdatingUrl flag for same-route filter updates
          // For navigation (when identifier is present), don't set the flag
          // This allows ROUTER_NAVIGATED -> loadFiltersFromUrl to run after navigation
          if (!action.identifier) {
            this.isUpdatingUrl = true;
          }
          const result = this.urlStateManager.updateUrl(action, currentRoute);

          if (action.identifier) {
            // Navigation case (e.g., dashboard chart click → device search)
            // Pre-populate the store with computed filters so the target component
            // has correct state immediately on init (before async URL deserialization)
            if (result) {
              return of(
                FilterActions.updateFiltersSuccess({
                  filters: result.filters,
                  filterBarId: result.filterBarId,
                })
              );
            }
            return EMPTY;
          }
          return of(FilterActions.updateFiltersSuccess(action));
        })
      )
    );
  }
}
